import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
} from "recharts";
import { DisaggregatedMetric, GeoDetails, GeoTag } from "../../services/types";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { formatValue, formatValueBase } from "../../utils/helpers";
import MetricMetadataTrigger from "../metrics/MetricMetadataTrigger";
import femaleSvg from '../../assets/female.svg';
import maleSvg from '../../assets/male.svg';

interface AgeByGenderChartProps {
    maleMetric?: DisaggregatedMetric;
    femaleMetric?: DisaggregatedMetric;
    height?: string;
    background?: string;
    maleColor?: string;
    femaleColor?: string;
    title?: string;
    subtitle?: string;
    metadataMetricId?: number;
    metadataMetricName?: string;
    disaggData?: DisaggregatedMetric[] | null;
    geoDetails?: GeoDetails[];
    geoTag?: GeoTag | null;
    selectionActive?: boolean;
    showMetadataIcon?: boolean;
}

type AgeRow = {
    name: string;
    male: number;
    female: number;
    unit?: string;
};

const AGE_ORDER = ["<20", "20-29", "30-44", "45-64", "65+"];

const normalizeAgeLabel = (value: string) => {
    const cleaned = value
        .replace(/\u00A0/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

    if (cleaned === "under 20") return "<20";

    return cleaned
    .replace(/^(\d+)\s*-\s*(\d+)$/, "$1-$2")
    .replace(/\b(\d+)\+\b/, "$1+");
};

const sortAgeLabels = (a: string, b: string) => {
    const aIndex = AGE_ORDER.indexOf(a);
    const bIndex = AGE_ORDER.indexOf(b);

    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    return a.localeCompare(b);
};

const buildAgeMap = (metric?: DisaggregatedMetric) => {
    const disaggregations = metric?.versions?.[0]?.disaggregations ?? [];
    const map = new Map<string, number>();

    for (const item of disaggregations) {
        const label = normalizeAgeLabel(item.name);
        map.set(label, item.value ?? 0);
    }

    return map;
};

const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    const unit = payload?.[0]?.payload?.unit;

    return (
        <div className="bg-white p-2 border border-slate-200 rounded shadow-sm text-xs">
        <p className="font-semibold mb-1">{label}</p>
            {payload.map((entry: any) => (
                <div
                    key={entry.dataKey}
                    className="flex items-center justify-between gap-4 py-0.5"
                >
                    <div className="flex items-center gap-2">
                        <span
                            className="w-2.5 h-2.5 rounded-full"
                            style={{ backgroundColor: entry.color }}
                        />
                        <span>{entry.name}</span>
                    </div>

                    <span className="font-medium">
                        {formatValue(entry.value, unit)}
                    </span>
                </div>
            ))}
        </div>
    );
};

const AgeByGenderChart = ({
    maleMetric,
    femaleMetric,
    height = "h-72",
    background = "bg-white",
    maleColor = "#1F3B73",
    femaleColor = "#A61B1B",
    title = "Age by Gender",
    subtitle,
    metadataMetricId,
    metadataMetricName = title,
    disaggData = null,
    geoDetails = [],
    geoTag = null,
    selectionActive = false,
    showMetadataIcon = false,
}: AgeByGenderChartProps) => {
    const maleReady = !!maleMetric?.versions?.[0]?.disaggregations;
    const femaleReady = !!femaleMetric?.versions?.[0]?.disaggregations;

    if (!maleReady && !femaleReady) {
        return (
        <div className="h-24 flex items-center justify-center text-xs text-slate-500">
            <Skeleton height={24} width="100%" />
        </div>
        );
    }

    const maleMap = buildAgeMap(maleMetric);
    const femaleMap = buildAgeMap(femaleMetric);

    const allLabels = Array.from(
        new Set([...maleMap.keys(), ...femaleMap.keys()])
    ).sort(sortAgeLabels);

    const unit =
        maleMetric?.versions?.[0]?.unit ?? femaleMetric?.versions?.[0]?.unit;

    const chartData: AgeRow[] = allLabels.map((label) => ({
        name: label,
        male: maleMap.get(label) ?? 0,
        female: femaleMap.get(label) ?? 0,
        unit,
    }));

    if (chartData.length === 0) {
        return (
        <div className="h-24 flex items-center justify-center text-xs text-slate-500">
            No data available
        </div>
        );
    }

    // Summary totals for male and female percent UI
    const maleTotal = chartData.reduce((sum, row) => sum + row.male, 0);
    const femaleTotal = chartData.reduce((sum, row) => sum + row.female, 0);
    const genderTotal = maleTotal + femaleTotal;

    const malePercent = genderTotal ? maleTotal / genderTotal : 0;
    const femalePercent = genderTotal ? femaleTotal / genderTotal : 0;

    return (
        <div className="relative overflow-hidden group hover:shadow-md transition-all border rounded-xl bg-white">
            <div className="p-3 bg-white">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-sm font-semibold text-port-text">{title}</h3>
                        {subtitle && <p className="text-port-text text-xs mt-1">{subtitle}</p>}
                    </div>

                    <MetricMetadataTrigger
                        metricId={metadataMetricId}
                        metricName={metadataMetricName}
                        disaggData={disaggData}
                        geoDetails={geoDetails}
                        geoTag={geoTag}
                        selectionActive={selectionActive}
                        showIcon={showMetadataIcon}
                    />
                </div>
                <div className="flex items-center justify-center gap-6 mt-3">
                    <div className="flex items-center gap-2">
                        <img src={femaleSvg} alt="Female" className="w-8 h-8" />
                        <span className="text-2xl font-bold text-port-text">
                            {formatValue(femalePercent, "percentage", 1360)}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <img src={maleSvg} alt="Male" className="w-8 h-8" />
                        <span className="text-2xl font-bold text-port-text">
                            {formatValue(malePercent, "percentage", 1367)}
                        </span>
                    </div>
                </div>
            </div>

            <div className={`${height} ${background} min-h-[220px] min-w-0 px-2 pb-3`}>
                <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={chartData}
                    margin={{ top: 8, right: 18, left: 8, bottom: 28 }}
                    barCategoryGap="22%"
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    />
                    <YAxis
                    tick={{ fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => formatValueBase(value, unit ?? "integer")}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                    verticalAlign="bottom"
                    height={20}
                    iconType="circle"
                    wrapperStyle={{ fontSize: "12px" }}
                    />
                    <Bar
                    dataKey="female"
                    name="Female"
                    fill={femaleColor}
                    radius={[4, 4, 0, 0]}
                    barSize={14}
                    />
                    <Bar
                    dataKey="male"
                    name="Male"
                    fill={maleColor}
                    radius={[4, 4, 0, 0]}
                    barSize={14}
                    isAnimationActive={false}
                    />
                </BarChart>
                </ResponsiveContainer>
            </div>

            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2258e5]/50 to-[#00ac4e]/50 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
    );
};

export default AgeByGenderChart;